import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api, fetchBlob } from '../lib/api';
import { AssuranceNotice, Banner, Field, Loading } from '../components/ui';
import { BRAND_NAME, BrandMark } from '../components/Layout';
import { formatDateTime } from '../lib/format';

/**
 * Recipient copy retrieval.
 *
 * The emailed link identifies the agreement, but releasing the signed document
 * requires a fresh one-time passcode (F13: reauthentication for sensitive
 * retrieval). A link that stays valid for weeks is not sufficient on its own.
 */
export function Completed() {
  const { token = '' } = useParams();
  const [data, setData] = useState<any>(null);
  const [challenge, setChallenge] = useState<any>(null);
  const [session, setSession] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/completed/${token}`).then(setData).catch((cause) => {
      setError(cause instanceof ApiError ? cause.message : 'This link is not valid.');
    });
  }, [token]);

  const requestCode = async () => {
    setBusy(true); setError(null);
    try {
      setChallenge(await api.post(`/completed/${token}/auth/start`));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not send a code.');
    } finally { setBusy(false); }
  };

  const verify = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const result = await api.post(`/completed/${token}/auth/verify`,
        { challenge_id: challenge.challenge_id, code });
      setSession(result.session_token);
      setCode('');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That code did not work.');
    } finally { setBusy(false); }
  };

  const download = async () => {
    if (!session) return;
    setBusy(true); setError(null);
    try {
      const { blob } = await fetchBlob(`/completed/${token}/document`,
                                       { 'x-signing-session': session });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${data?.envelope?.title || 'agreement'} (signed).pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'The download failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <header className="topbar">
        <div className="shell shell--narrow topbar__inner">
          <span className="brand">
            <BrandMark />
            {data?.organization?.name || BRAND_NAME}
          </span>
        </div>
      </header>
      <main className="shell shell--narrow section" style={{ flex: 1 }}>
        {error && !data ? (
          <Banner tone="danger" title="This link cannot be opened">{error}</Banner>
        ) : !data ? <Loading /> : (
          <div className="stack">
            <div className="card stack center">
              <div style={{ fontSize: '2.2rem' }} aria-hidden="true">✓</div>
              <h1 style={{ margin: 0, fontSize: '1.3rem' }}>{data.envelope.title}</h1>
              <p className="muted small" style={{ margin: 0 }}>
                Completed {formatDateTime(data.envelope.completed_at)} ·{' '}
                for {data.recipient.name}
              </p>
            </div>

            {error && <Banner tone="danger" title="Please check this">{error}</Banner>}

            {session ? (
              <div className="card stack">
                <Banner tone="ok" title="Verified">
                  Your identity is confirmed for this session.
                </Banner>
                <button className="btn btn--primary btn--large btn--block"
                        disabled={busy} onClick={() => void download()}>
                  {busy ? 'Preparing…' : 'Download your signed copy'}
                </button>
                <p className="small muted" style={{ margin: 0 }}>
                  The download includes the signed document and its certificate of
                  completion.
                </p>
              </div>
            ) : !challenge ? (
              <div className="card stack">
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Verify it is you</h2>
                <p className="muted small" style={{ margin: 0 }}>
                  Signed agreements are released only after a fresh check. We will
                  email a one-time code to {data.recipient_masked}.
                </p>
                <button className="btn btn--primary btn--large btn--block"
                        disabled={busy} onClick={() => void requestCode()}>
                  {busy ? 'Sending…' : 'Email me a code'}
                </button>
              </div>
            ) : (
              <form className="card stack" onSubmit={verify}>
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Enter your code</h2>
                <p className="muted small" style={{ margin: 0 }}>
                  Sent to <strong>{challenge.destination_mask}</strong>.
                </p>
                <Field label="One-time code" id="dl-otp" required>
                  <input id="dl-otp" className="input" inputMode="numeric"
                         autoComplete="one-time-code" maxLength={6} required
                         value={code}
                         style={{ fontSize: '1.4rem', letterSpacing: '.3em',
                                  textAlign: 'center' }}
                         onChange={(event) =>
                           setCode(event.target.value.replace(/\D/g, ''))} />
                </Field>
                <button className="btn btn--primary btn--large btn--block"
                        disabled={busy || code.length !== 6}>
                  {busy ? 'Checking…' : 'Verify and continue'}
                </button>
                <button type="button" className="btn btn--ghost btn--block"
                        disabled={busy} onClick={() => void requestCode()}>
                  Send a new code
                </button>
              </form>
            )}

            <AssuranceNotice profile={data.envelope.assurance_profile} />
            {/* "whatever happens to any account" invited exactly the doubt it
                was meant to settle. The useful fact is that the download is a
                complete, self-verifying record -- worth keeping for the same
                reason any signed agreement is. */}
            <Banner tone="info" title="This copy is yours">
              It is a complete record of what was signed, and it verifies on its
              own. Save it wherever you keep your agreements.
            </Banner>
          </div>
        )}
      </main>
    </div>
  );
}
